/**
 * Supabase Support Messages API Service
 *
 * Handles all CRUD operations for support messages from the /support page
 */

import { supabase } from './client';
import type { SupportMessage, SupportMessageInsert, SupportMessageUpdate } from './database.types';

// ============================================
// Constants
// ============================================

export const supportTopicLabels: Record<SupportMessage['support_topic'], string> = {
  'app-help': 'App / Controller Help',
  'technical': 'Technical Issue',
  'warranty': 'Warranty Claim',
  'scheduling': 'Scheduling / Appointments',
  'billing': 'Billing Question',
  'general': 'General Inquiry',
};

export const supportStatusLabels: Record<SupportMessage['status'], string> = {
  new: 'New',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const supportStatusColors: Record<SupportMessage['status'], string> = {
  new: '#3B82F6', // blue
  in_progress: '#F59E0B', // amber
  resolved: '#10B981', // green
  closed: '#6B7280', // gray
};

export const supportPriorityLabels: Record<SupportMessage['priority'], string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

export const supportPriorityColors: Record<SupportMessage['priority'], string> = {
  low: '#6B7280', // gray
  medium: '#3B82F6', // blue
  high: '#F59E0B', // amber
  urgent: '#EF4444', // red
};

// ============================================
// Frontend Data Type
// ============================================

export interface SupportMessageData {
  id: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  email: string;
  phone?: string;
  supportTopic: SupportMessage['support_topic'];
  message: string;
  status: SupportMessage['status'];
  priority: SupportMessage['priority'];
  assignedTo?: string;
  notes?: string;
  gmailThreadId?: string;
  resolvedAt?: string;
}

// ============================================
// Type Mappings
// ============================================

const mapToFrontend = (row: SupportMessage): SupportMessageData => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  name: row.name,
  email: row.email,
  phone: row.phone || undefined,
  supportTopic: row.support_topic,
  message: row.message,
  status: row.status,
  priority: row.priority,
  assignedTo: row.assigned_to || undefined,
  notes: row.notes || undefined,
  gmailThreadId: row.gmail_thread_id || undefined,
  resolvedAt: row.resolved_at || undefined,
});

const mapToDatabase = (data: Partial<SupportMessageData>): Partial<SupportMessageUpdate> => {
  const dbData: Partial<SupportMessageUpdate> = {};

  if (data.name !== undefined) dbData.name = data.name;
  if (data.email !== undefined) dbData.email = data.email;
  if (data.phone !== undefined) dbData.phone = data.phone || null;
  if (data.supportTopic !== undefined) dbData.support_topic = data.supportTopic;
  if (data.message !== undefined) dbData.message = data.message;
  if (data.status !== undefined) dbData.status = data.status;
  if (data.priority !== undefined) dbData.priority = data.priority;
  if (data.assignedTo !== undefined) dbData.assigned_to = data.assignedTo || null;
  if (data.notes !== undefined) dbData.notes = data.notes || null;
  if (data.gmailThreadId !== undefined) dbData.gmail_thread_id = data.gmailThreadId || null;
  if (data.resolvedAt !== undefined) dbData.resolved_at = data.resolvedAt || null;

  return dbData;
};

// ============================================
// API Functions
// ============================================

export interface GetSupportMessagesParams {
  status?: SupportMessage['status'] | SupportMessage['status'][];
  priority?: SupportMessage['priority'] | SupportMessage['priority'][];
  supportTopic?: SupportMessage['support_topic'];
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: 'created_at' | 'updated_at' | 'priority' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export interface GetSupportMessagesResponse {
  messages: SupportMessageData[];
  total: number;
  page: number;
  pageSize: number;
}

export const supportMessagesApi = {
  /**
   * Create a new support message
   */
  async create(data: {
    name: string;
    email: string;
    phone?: string;
    supportTopic: SupportMessage['support_topic'];
    message: string;
  }): Promise<SupportMessageData | null> {
    const insertData: SupportMessageInsert = {
      name: data.name,
      email: data.email,
      phone: data.phone || null,
      support_topic: data.supportTopic,
      message: data.message,
      status: 'new',
      priority: 'medium',
    };

    // console.log('supportMessagesApi.create - Inserting data:', insertData);

    const { data: result, error } = await supabase
      .from('support_messages')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      // console.error('Error creating support message:', {
      //   message: error.message,
      //   code: error.code,
      //   details: error.details,
      //   hint: error.hint,
      // });
      return null;
    }

    // console.log('supportMessagesApi.create - Success, result:', result);
    return mapToFrontend(result as SupportMessage);
  },

  /**
   * Get all support messages with filtering and pagination
   */
  async getAll(params: GetSupportMessagesParams = {}): Promise<GetSupportMessagesResponse> {
    const {
      status,
      priority,
      supportTopic,
      search,
      page = 1,
      pageSize = 20,
      sortBy = 'created_at',
      sortOrder = 'desc',
    } = params;

    // console.log('supportMessagesApi.getAll - Fetching messages with params:', params);

    let query = supabase.from('support_messages').select('*', { count: 'exact' });

    // Filter by status
    if (status) {
      if (Array.isArray(status)) {
        query = query.in('status', status);
      } else {
        query = query.eq('status', status);
      }
    }

    // Filter by priority
    if (priority) {
      if (Array.isArray(priority)) {
        query = query.in('priority', priority);
      } else {
        query = query.eq('priority', priority);
      }
    }

    // Filter by support topic
    if (supportTopic) {
      query = query.eq('support_topic', supportTopic);
    }

    // Search by name, email, or message
    if (search) {
      query = query.or(`name.ilike.%${search}%,email.ilike.%${search}%,message.ilike.%${search}%`);
    }

    // Sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // Pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      // console.error('Error fetching support messages:', {
      //   message: error.message,
      //   code: error.code,
      //   details: error.details,
      //   hint: error.hint,
      // });
      return { messages: [], total: 0, page, pageSize };
    }

    // console.log('supportMessagesApi.getAll - Found', data?.length || 0, 'messages, total:', count);

    return {
      messages: (data as SupportMessage[]).map(mapToFrontend),
      total: count || 0,
      page,
      pageSize,
    };
  },

  /**
   * Get a single support message by ID
   */
  async getById(id: string): Promise<SupportMessageData | null> {
    const { data, error } = await supabase
      .from('support_messages')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      // console.error('Error fetching support message:', error);
      return null;
    }

    return mapToFrontend(data as SupportMessage);
  },

  /**
   * Update a support message
   */
  async update(id: string, data: Partial<SupportMessageData>): Promise<SupportMessageData | null> {
    const updateData = mapToDatabase(data);

    const { data: result, error } = await supabase
      .from('support_messages')
      .update({ ...updateData, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      // console.error('Error updating support message:', error);
      return null;
    }

    return mapToFrontend(result as SupportMessage);
  },

  /**
   * Delete a support message
   */
  async delete(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('support_messages')
      .delete()
      .eq('id', id);

    if (error) {
      // console.error('Error deleting support message:', error);
      return false;
    }

    return true;
  },

  /**
   * Mark a support message as resolved
   */
  async markResolved(id: string): Promise<SupportMessageData | null> {
    return this.update(id, {
      status: 'resolved',
      resolvedAt: new Date().toISOString(),
    });
  },

  /**
   * Get count of new/unresolved messages
   */
  async getUnresolvedCount(): Promise<number> {
    const { count, error } = await supabase
      .from('support_messages')
      .select('*', { count: 'exact', head: true })
      .in('status', ['new', 'in_progress']);

    if (error) {
      // console.error('Error getting unresolved count:', error);
      return 0;
    }

    return count || 0;
  },
};

export default supportMessagesApi;
